import type {
  AdminUserWithNurses,
  CreateUserInput,
  Invitation,
  InvitationWithCompany,
  UpdateUserInput,
  UserSetting,
  WithdrawMyAccountInput,
  WithdrawMyAccountResult,
} from "dutyon-server/types";
import type { MeData } from "../auth/useAuth";
import { httpClient } from "./httpClient";

export const getMe = async (): Promise<MeData | null> => {
  const response = await httpClient.get<MeData | null>("/users/me");
  return response.data;
};

export const getUserLastInvitation = async (): Promise<InvitationWithCompany | null> => {
  const response = await httpClient.get<InvitationWithCompany | null>("/users/me/invitations/last");
  return response.data;
};

export const cancelMyInvitation = async (invitationId: string): Promise<Invitation> => {
  const response = await httpClient.delete<Invitation>(`/users/me/invitations/${invitationId}`);
  return response.data;
};

export const getMySettings = async (): Promise<UserSetting> => {
  const response = await httpClient.get<UserSetting>("/users/me/settings");
  return response.data;
};

export const updateMySettings = (data: Partial<UserSetting>): Promise<UserSetting> =>
  updateMySettingsForSession(data);

export const updateMySettingsForSession = async (
  data: Partial<UserSetting>,
  sessionToken?: string,
): Promise<UserSetting> => {
  const config = sessionToken
    ? { headers: { Authorization: `Bearer ${sessionToken}` }, _retry: true }
    : undefined;
  const response = await httpClient.patch<UserSetting>("/users/me/settings", data, config);
  return response.data;
};

export const withdrawMyAccount = async (
  input: WithdrawMyAccountInput,
): Promise<WithdrawMyAccountResult> => {
  const response = await httpClient.post<WithdrawMyAccountResult>("/users/me/withdrawal", input);
  return response.data;
};

export const linkMyFirebaseAccount = async (token: string): Promise<AdminUserWithNurses> => {
  const response = await httpClient.post<AdminUserWithNurses>("/users/me/firebase-link", { token });
  return response.data;
};

export const unlinkMyFirebaseAccount = async (): Promise<AdminUserWithNurses> => {
  const response = await httpClient.delete<AdminUserWithNurses>("/users/me/firebase-link");
  return response.data;
};

export interface FindAllUsersResponse {
  list: AdminUserWithNurses[];
  total: number;
}

export interface UserSearchFilter {
  name?: string;
  email?: string;
  phone?: string;
  query?: string;
  teamId?: string;
}

export const findAllUsers = async ({
  page,
  limit,
  filter,
}: {
  page: number;
  limit: number;
  filter?: UserSearchFilter;
}): Promise<FindAllUsersResponse> => {
  const response = await httpClient.get<FindAllUsersResponse>("/users", {
    params: { page, limit, ...filter },
  });
  return response.data;
};

export const findOneUser = async (id: string): Promise<AdminUserWithNurses | null> => {
  const response = await httpClient.get<AdminUserWithNurses | null>(`/users/${id}`);
  return response.data;
};

export const createUser = async (data: CreateUserInput): Promise<AdminUserWithNurses> => {
  const response = await httpClient.post<AdminUserWithNurses>("/users", data);
  return response.data;
};

export const updateUser = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateUserInput;
}): Promise<AdminUserWithNurses> => {
  const response = await httpClient.put<AdminUserWithNurses>(`/users/${id}`, data);
  return response.data;
};

export const removeUser = async (id: string): Promise<{ id: string }> => {
  const response = await httpClient.delete<{ id: string }>(`/users/${id}`);
  return response.data;
};

export const updateUserAdminStatus = async ({
  id,
  isAdmin,
}: {
  id: string;
  isAdmin: boolean;
}): Promise<AdminUserWithNurses> => {
  const response = await httpClient.patch<AdminUserWithNurses>(`/users/${id}/admin`, { isAdmin });
  return response.data;
};
